import { Panel, PanelProps, useReactFlow, useStore, useViewport } from "@xyflow/react"
import { Button } from "design-system/atoms/Button"
import { Slider } from "design-system/atoms/Slider"
import { cn } from "design-system/utils"
import { Maximize, Minus, Plus } from "lucide-react"
import { forwardRef } from "react"

const DURATION = 300

export const ZoomSlider = forwardRef<
  HTMLDivElement,
  Omit<PanelProps, "children"> & { hideSlider?: boolean }
>(({ className, hideSlider = false, ...props }, ref) => {
  const { zoom } = useViewport()
  const { zoomTo, getZoom, fitView } = useReactFlow()

  const { minZoom, maxZoom } = useStore(
    (state) => ({
      minZoom: state.minZoom,
      maxZoom: state.maxZoom,
    }),
    (a, b) => a.minZoom !== b.minZoom || a.maxZoom !== b.maxZoom,
  )

  return (
    <Panel
      className={cn(
        "flex items-center rounded-md bg-background h-10 px-1 text-foreground border border-border",
        className,
      )}
      {...props}
      ref={ref}
    >
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => zoomTo(getZoom() - 0.2, { duration: DURATION })}
      >
        <Minus className="h-4 w-4" />
      </Button>
      {!hideSlider && (
        <Slider
          className="w-[140px]"
          value={[zoom]}
          min={minZoom}
          max={maxZoom}
          step={0.01}
          onValueChange={(values) => zoomTo(values[0] ?? 0)}
        />
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => zoomTo(getZoom() + 0.2, { duration: DURATION })}
      >
        <Plus className="h-4 w-4" />
      </Button>
      <Button
        className="h-8 px-2 tabular-nums"
        variant="ghost"
        onClick={() => zoomTo(1, { duration: DURATION })}
      >
        {`${(100 * zoom).toFixed(0)}%`}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => fitView({ duration: 300, padding: 0.25 })}
      >
        <Maximize className="h-4 w-4" />
      </Button>
    </Panel>
  )
})

ZoomSlider.displayName = "ZoomSlider"
